import { Outlet, Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemIcon,
} from "@mui/material";
import { useUser } from "./UserContext";
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  ManageAccounts as ManageAccountsIcon,
  AccountTree as AccountTreeIcon,
  Create as CreateIcon,
} from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useCallback, useRef, useEffect } from "react";
import AssistantIcon from "@mui/icons-material/Assistant";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PsychologyIcon from "@mui/icons-material/Psychology";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AdminPanelSettings from "@mui/icons-material/AdminPanelSettings";
import FilterListIcon from "@mui/icons-material/FilterList";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AutoAwesomeMosaicIcon from "@mui/icons-material/AutoAwesomeMosaic";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import KeyIcon from "@mui/icons-material/Key";
import LockIcon from "@mui/icons-material/Lock";
import ChangePasswordDialog from "./ChangePasswordDialog.tsx";
import ForcePasswordChangeOverlay from "./ForcePasswordChangeOverlay.tsx";
import { useFeatureFlags } from "../context/FeatureFlagContext.tsx";

const drawerWidth = 270;

export default function Layout() {
  const { user, logout, requiresPasswordChange, clearPasswordChangeFlag } =
    useUser();
  const { flags } = useFeatureFlags();
  const location = useLocation();
  const appBarRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [profilePanelOpen, setProfilePanelOpen] = useState(false);
  const [appBarHeight, setAppBarHeight] = useState(0);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] =
    useState(false);

  // Measure AppBar height on mount and when it changes
  useEffect(() => {
    const measureAppBarHeight = () => {
      if (appBarRef.current) {
        setAppBarHeight(appBarRef.current.offsetHeight);
      }
    };

    // Measure immediately
    measureAppBarHeight();

    // Re-measure on window resize
    window.addEventListener("resize", measureAppBarHeight);

    return () => {
      window.removeEventListener("resize", measureAppBarHeight);
    };
  }, []);

  const getNavSections = useCallback(() => {
    return [
      {
        label: "Dashboard",
        to: "/dashboard",
        icon: <DashboardIcon />,
      },
      {
        label: "Agents",
        icon: <AssistantIcon />,
        items: [
          { label: "Catalog", to: "/agents", icon: <MenuBookIcon /> },
          // { label: "Create New Agent", to: "/agent/new" },
          ...(user?.role === "admin"
            ? [
                {
                  label: "Agent Creator",
                  to: "/agent/conversational",
                  icon: <SmartToyIcon />,
                },
              ]
            : []),
        ],
      },
      {
        label: "Tools",
        icon: <AutoAwesomeMosaicIcon />,
        items: [{ label: "Catalog", to: "/tools", icon: <StorefrontIcon /> }],
      },
      {
        label: "Knowledge Spaces",
        icon: <PsychologyIcon />,
        items: [
          { label: "Collections", to: "/kb", icon: <MenuBookIcon /> },
          // ...(user?.role === 'admin' ? [{ label: "Code Indexer", to: "/kb/code-indexer", icon: <AssistantIcon /> }] : []),
          {
            label: "Builder",
            to: "/kb/document-indexer",
            icon: <LibraryAddIcon />,
          },
        ],
      },
      ...((user?.role === "admin" || user?.role === "operator-admin") &&
      flags.enableWorkflows
        ? [
            {
              label: "Workflows",
              icon: <AccountTreeIcon />,
              items: [
                { label: "Catalog", to: "/workflows", icon: <MenuBookIcon /> },
                {
                  label: "Workflow Generator",
                  to: "/workflow/generate",
                  icon: <CreateIcon />,
                },
                {
                  label: "Approvals",
                  to: "/workflow/approvals",
                  icon: <AssistantIcon />,
                },
                // { label: "Workflow Orchestrator", to: "/workflow/orchestrator", icon: <AddCircleIcon /> },
              ],
            },
          ]
        : []),
      ...(user?.role === "admin" || user?.role === "operator-admin"
        ? [
            {
              label: "User Management",
              icon: <ManageAccountsIcon />,
              items: [
                { label: "List Users", to: "/users", icon: <PeopleIcon /> },
                { label: "Add User", to: "/add-user", icon: <PersonAddIcon /> },
              ],
            },
          ]
        : []),
      ...(user?.role === "admin" || user?.role === "operator-admin"
        ? [
            {
              label: "GRC Operations",
              icon: <AdminPanelSettings />,
              items: [
                {
                  label: "Audit Logs",
                  to: "/audit-logs",
                  icon: <FilterListIcon />,
                },
              ],
            },
          ]
        : []),
    ];
  }, [user, flags.enableWorkflows]);

  const navSections = getNavSections();
  const [expanded, setExpanded] = useState(
    () => new Set([navSections[0].label]),
  );

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(panel);
      } else {
        newSet.delete(panel);
      }
      return newSet;
    });
  };

  // Handle successful password change from overlay
  const handlePasswordChangeSuccess = () => {
    clearPasswordChangeFlag();
  };

  // Handle logout from overlay
  const handlePasswordChangeLogout = () => {
    logout();
  };

  return (
    <Box sx={{ display: "flex" }}>
      {/* Force Password Change Overlay */}
      {requiresPasswordChange && (
        <ForcePasswordChangeOverlay
          onSuccess={handlePasswordChangeSuccess}
          onLogout={handlePasswordChangeLogout}
        />
      )}

      <AppBar
        ref={appBarRef}
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        style={{ backgroundColor: "#000" }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            edge="start"
            sx={{ mr: 2 }}
          >
            {drawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          {/* <img src="nextmoca-logotext.png" alt="Next Moca Logo Text" style={{ height: 90, marginRight: 16 }} /> */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mr: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: "#FAF0E6",
                pl: 0.9,
                pr: 1.8,
                borderRadius: 50,
                display: "inline-flex",
                alignItems: "center", // This centers the image "box"
                justifyContent: "center",
                height: "32px",
                overflow: "hidden",
                mr: 2,
                mb: 0.7,
              }}
            >
              <img
                src="/AurivioWordmarkLogo-newbg.svg"
                alt="Aurivio Wordmark Logo"
                style={{
                  height: "180%",
                  width: "auto",
                  marginLeft: "-5px",
                  marginRight: "-45px",
                  display: "block",
                  // --- MOVE LETTERS DOWN ---
                  position: "relative",
                  top: "4px", // push the letters lower inside the white area
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "#B87333", // Copper color
                fontWeight: "bold",
                fontSize: "0.72rem",
                lineHeight: 1,
                letterSpacing: "0.1em",
              }}
            >
              Powered by Next Moca
            </Typography>
          </Box>

          

          <Box sx={{ flexGrow: 1 }} />
          {user && (
            <>
              <IconButton
                color="inherit"
                onClick={() => setProfilePanelOpen(open => !open)}
              >
                <SettingsIcon />
              </IconButton>
              <Button
                color="inherit"
                // onClick={() => setProfilePanelOpen(open => !open)}
                sx={{ ml: 1, textTransform: "none" }}
              >
                {user.username}
              </Button>
              <Drawer
                anchor="right"
                open={profilePanelOpen}
                onClose={() => setProfilePanelOpen(false)}
                PaperProps={{
                  sx: {
                    width: "27%",
                    top: `${appBarHeight}px`, // Dynamically position below the toolbar
                    // height: `calc(100% - ${appBarHeight}px)`,  // Adjust height to account for toolbar
                    height: "36%", // Adjust height to account for toolbar
                    pt: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {user.username}
                  </Typography>
                  <IconButton
                    onClick={() => setProfilePanelOpen(false)}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <List>
                  {/* TODO: Not implemented yet
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/profile" onClick={() => setProfilePanelOpen(false)}>
                      <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                        <AccountCircleIcon />
                      </ListItemIcon>
                      <ListItemText primary="Your Profile" />
                    </ListItemButton>
                  </ListItem> */}
                  <ListItem disablePadding>
                    <ListItemButton
                      component={Link}
                      to="/config"
                      onClick={() => setProfilePanelOpen(false)}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 40, color: "primary.main" }}
                      >
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Config" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      component={Link}
                      to="/key-manager"
                      onClick={() => setProfilePanelOpen(false)}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 40, color: "primary.main" }}
                      >
                        <KeyIcon />
                      </ListItemIcon>
                      <ListItemText primary="Key Manager" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        setProfilePanelOpen(false);
                        setChangePasswordDialogOpen(true);
                      }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 40, color: "primary.main" }}
                      >
                        <LockIcon />
                      </ListItemIcon>
                      <ListItemText primary="Change Password" />
                    </ListItemButton>
                  </ListItem>
                  <Divider sx={{ my: 1 }} />
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        setProfilePanelOpen(false);
                        logout();
                      }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 40, color: "primary.main" }}
                      >
                        <LogoutIcon />
                      </ListItemIcon>
                      <ListItemText primary="Logout" />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Drawer>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={drawerOpen}
        sx={{
          width: drawerOpen ? drawerWidth : 0,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerOpen ? drawerWidth : 0,
            boxSizing: "border-box",
            transition: "width 0.2s",
            overflowX: "hidden",
            borderRight: drawerOpen ? undefined : "none",
          },
        }}
      >
        {drawerOpen && (
          <>
            <Toolbar />
            <Box sx={{ width: drawerWidth, pt: 4.5 }}>
              {getNavSections(user)?.map((section) =>
                !section.items ? (
                  /* Top-level link — no accordion */
                  <ListItem
                    key={section.label}
                    disablePadding
                    sx={{ mb: 1.25 }}
                  >
                    <ListItemButton
                      component={Link}
                      to={section.to}
                      selected={location.pathname === section.to}
                      sx={{
                        minHeight: 30,
                        pl: 2.5,
                        py: 0.75,
                        borderRadius: 1,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 3,
                          color: "#B87333",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={section.label}
                        primaryTypographyProps={{ fontWeight: "bold" }}
                      />
                    </ListItemButton>
                  </ListItem>
                ) : (
                  /* Accordion for sections with children */
                  <Accordion
                    disableGutters
                    key={section.label}
                    expanded={expanded.has(section.label) && drawerOpen}
                    onChange={handleAccordionChange(section.label)}
                    sx={{
                      boxShadow: "none",
                      background: "transparent",
                      "&:before": { display: "none" },
                      mb: 1.25,
                    }}
                    disabled={!drawerOpen}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`${section.label}-content`}
                      id={`${section.label}-header`}
                      sx={{
                        minHeight: 30,
                        pl: 2.5,
                        borderRadius: 1,
                        bgcolor:
                          section.items?.some(
                            (i) => location.pathname === i.to,
                          ) && !expanded.has(section.label)
                            ? "action.selected"
                            : "transparent",
                        "& .MuiAccordionSummary-content": {
                          my: 0.5,
                          alignItems: "center",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 3,
                          color: "#B87333",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={section.label}
                        primaryTypographyProps={{ fontWeight: "bold" }}
                      />
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, pt: 0 }}>
                      <List dense sx={{ py: 0 }}>
                        {section.items?.map((item) => (
                          <ListItem
                            key={item.to}
                            disablePadding
                            sx={{ display: "block", pl: 6, py: 0 }}
                          >
                            <ListItemButton
                              component={Link}
                              to={item.to}
                              selected={location.pathname === item.to}
                              sx={{
                                minHeight: 30,
                                justifyContent: drawerOpen
                                  ? "initial"
                                  : "center",
                                px: 2.5,
                                py: 0.5,
                                borderRadius: 1,
                                "&.Mui-selected": {
                                  bgcolor: "action.selected",
                                },
                                "&.Mui-selected:hover": {
                                  bgcolor: "action.selected",
                                },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 0,
                                  mr: 1.5,
                                  color: "#B87333",
                                }}
                              >
                                {item.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={drawerOpen ? item.label : ""}
                                primaryTypographyProps={{
                                  fontWeight: "bold",
                                  fontSize: "0.80rem",
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ),
              )}
            </Box>
          </>
        )}
      </Drawer>
      <Box
        component="main"
        sx={{ flexGrow: 1, pt: 9, pr: 3, pb: 3, pl: 3, mt: 3 }}
        // style={{border: "solid 2px red"}}
      >
        {/* <Container maxWidth="lg" style={{border: "solid 2px red"}}> */}

        <Outlet />
        {/* </Container> */}
      </Box>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={changePasswordDialogOpen}
        onClose={() => setChangePasswordDialogOpen(false)}
      />
    </Box>
  );
}
